import React, { useRef, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { Eye, EyeOff, Mail, Wifi, ShieldCheck, Gauge, Fingerprint } from 'lucide-react';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginEmail = () => {
  const { login, sessionExpired } = useAuth();
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const [formData, setFormData] = useState({
    corporate_email: '',
    senha: ''
  });
  const [fieldErrors, setFieldErrors] = useState({
    corporate_email: '',
    senha: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const heroHighlights = [
    {
      Icon: ShieldCheck,
      title: 'Segurança ativa',
      text: 'Autenticação protegida e monitorada em tempo real.'
    },
    {
      Icon: Gauge,
      title: 'Performance consistente',
      text: 'Experiência otimizada para mobile e desktop.'
    },
    {
      Icon: Fingerprint,
      title: 'Acesso governado',
      text: 'Disponível apenas para perfis corporativos autorizados.'
    }
  ];

  const validateLoginForm = () => {
    const nextErrors = {
      corporate_email: '',
      senha: ''
    };

    const normalizedEmail = formData.corporate_email.trim();

    if (!normalizedEmail) {
      nextErrors.corporate_email = 'Informe seu e-mail corporativo.';
    } else if (!emailRegex.test(normalizedEmail)) {
      nextErrors.corporate_email = 'Informe um e-mail válido.';
    }

    if (!formData.senha) {
      nextErrors.senha = 'Informe sua senha.';
    }

    return nextErrors;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'corporate_email' && value.length > 255) return;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }

    if (error) {
      setError('');
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    if (loading) return;

    const nextErrors = validateLoginForm();
    const hasErrors = Object.values(nextErrors).some(Boolean);
    if (hasErrors) {
      setFieldErrors(nextErrors);

      if (nextErrors.corporate_email) {
        emailInputRef.current?.focus();
      } else if (nextErrors.senha) {
        passwordInputRef.current?.focus();
      }
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await login(formData.corporate_email.trim(), formData.senha);
      if (!result.success) {
        setError(result.message || 'Credenciais inválidas. Verifique seu e-mail e senha.');
      }
    } catch (err) {
      let errorMessage = 'Erro ao fazer login. Tente novamente.';

      if (err.response?.status === 401) {
        errorMessage = 'E-mail ou senha incorretos. Verifique suas credenciais.';
      } else if (err.response?.status === 422) {
        errorMessage = 'Dados inválidos. Verifique o formato do e-mail.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordKeyboardState = (event) => {
    setCapsLockOn(event.getModifierState('CapsLock'));
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#040617] text-slate-100">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(139,92,246,0.22),transparent_38%),radial-gradient(circle_at_85%_85%,rgba(99,102,241,0.2),transparent_40%)]" />
      </div>

      <div className="relative mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-6 sm:px-6 lg:px-10">
        <div className="grid w-full items-center gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-10">
          <section className="hidden lg:flex lg:flex-col lg:space-y-10">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-violet-100">
              <Wifi className="h-4 w-4 text-violet-300" aria-hidden="true" />
              Grupo Horizon
            </div>

            <div className="space-y-4">
              <h1 className="max-w-xl text-6xl font-semibold leading-[1.04] tracking-tight text-slate-100">
                Dashboard Executivo
              </h1>
              <p className="max-w-lg text-lg leading-relaxed text-slate-300">
                Acesso corporativo para gestão estratégica com segurança contínua e operação previsível.
              </p>
            </div>

            <div className="space-y-3">
              {heroHighlights.map(({ Icon, title, text }) => (
                <article key={title} className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4">
                  <div className="flex items-start gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/10 text-violet-200">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
                      <p className="mt-1 text-sm text-slate-300">{text}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="w-full">
            <div className="login-static-surface relative mx-auto w-full max-w-xl overflow-hidden rounded-[28px] border border-white/15 bg-slate-950/70 p-6 shadow-[0_24px_80px_-36px_rgba(15,23,42,0.75)] backdrop-blur-xl sm:border-slate-200/90 sm:bg-white sm:p-9 sm:shadow-[0_24px_80px_-36px_rgba(15,23,42,0.55)]">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-violet-400 via-indigo-500 to-fuchsia-500" />

              <div className="text-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-200 sm:bg-slate-100 sm:text-slate-600 lg:hidden">
                  <Wifi className="h-3.5 w-3.5" aria-hidden="true" />
                  Grupo Horizon
                </div>
                <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-100 sm:text-4xl sm:text-slate-900">Dashboard Executivo</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-slate-600">
                  Entre com seu e-mail corporativo para acessar o sistema.
                </p>
              </div>

              <form className="mt-8 flex flex-col space-y-6" onSubmit={handleLogin} noValidate>
                <div className="space-y-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-200 sm:text-slate-600">
                      E-mail Corporativo
                    </label>
                    <div className="relative mt-2">
                      <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                      <input
                        ref={emailInputRef}
                        id="email"
                        name="corporate_email"
                        type="email"
                        inputMode="email"
                        autoComplete="username"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        className={`login-static-input min-h-[48px] w-full rounded-2xl border bg-slate-50/80 px-4 py-3 pl-12 text-slate-900 placeholder-slate-400 shadow-sm transition focus:bg-white focus:outline-none focus:ring-2 ${
                          fieldErrors.corporate_email
                            ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-500'
                            : 'border-slate-200 focus:border-indigo-500 focus:ring-indigo-500'
                        }`}
                        placeholder="seu.email@horizon.com"
                        value={formData.corporate_email}
                        onChange={handleChange}
                        aria-invalid={Boolean(fieldErrors.corporate_email)}
                        aria-describedby={fieldErrors.corporate_email ? 'email-error' : undefined}
                      />
                    </div>
                    {fieldErrors.corporate_email && (
                      <p id="email-error" className="mt-2 text-sm text-rose-700" role="alert">
                        {fieldErrors.corporate_email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="senha" className="block text-sm font-semibold text-slate-200 sm:text-slate-600">
                      Senha
                    </label>
                    <div className="relative mt-2">
                      <input
                        ref={passwordInputRef}
                        id="senha"
                        name="senha"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        className={`login-static-input min-h-[48px] w-full rounded-2xl border bg-slate-50/80 px-4 py-3 pr-14 text-slate-900 placeholder-slate-400 shadow-sm transition focus:bg-white focus:outline-none focus:ring-2 ${
                          fieldErrors.senha
                            ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-500'
                            : 'border-slate-200 focus:border-indigo-500 focus:ring-indigo-500'
                        }`}
                        placeholder="Digite sua senha"
                        value={formData.senha}
                        onChange={handleChange}
                        onKeyDown={handlePasswordKeyboardState}
                        onKeyUp={handlePasswordKeyboardState}
                        onBlur={() => setCapsLockOn(false)}
                        aria-invalid={Boolean(fieldErrors.senha)}
                        aria-describedby={`${fieldErrors.senha ? 'senha-error' : ''}${capsLockOn ? `${fieldErrors.senha ? ' ' : ''}caps-lock-warning` : ''}` || undefined}
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        aria-pressed={showPassword}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    {fieldErrors.senha && (
                      <p id="senha-error" className="mt-2 text-sm text-rose-700" role="alert">
                        {fieldErrors.senha}
                      </p>
                    )}
                    {capsLockOn && (
                      <p
                        id="caps-lock-warning"
                        className="mt-2 text-sm font-medium text-amber-300 sm:text-amber-700"
                        role="status"
                        aria-live="polite"
                      >
                        Caps Lock ligado.
                      </p>
                    )}
                  </div>
                </div>

                {sessionExpired && (
                  <div
                    className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900 shadow-sm"
                    role="status"
                  >
                    Sua sessão expirou. Por favor, faça login novamente.
                  </div>
                )}

                {error && (
                  <div
                    className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 shadow-sm"
                    role="alert"
                    aria-live="assertive"
                  >
                    <p>{error}</p>
                    <p className="mt-1 text-xs text-rose-700">Revise seus dados e tente novamente.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="min-h-[48px] w-full rounded-2xl bg-gradient-to-r from-violet-600 via-indigo-600 to-fuchsia-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-500/35 transition duration-200 hover:translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {loading ? 'Entrando...' : 'Entrar no Dashboard'}
                </button>

                <p className="text-center text-xs leading-relaxed text-slate-400 sm:text-slate-500">
                  Problemas para acessar? Fale com o administrador da sua empresa.
                </p>
              </form>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LoginEmail;
